<template>
  <div class="image-carousel" v-if="images && images.length > 0">
    <div class="carousel-container">
      <!-- Main carousel wrapper -->
      <div class="carousel-wrapper" ref="carouselWrapper">
        <div 
          class="carousel-track" 
          :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
        >
          <div 
            v-for="(image, index) in images" 
            :key="index"
            class="carousel-slide"
          >
            <img 
              :src="typeof image === 'string' ? require(`../assets/img/${image}`) : require(`../assets/img/${image.src}`)" 
              :alt="image.alt || `Slide ${index + 1}`"
              class="carousel-image"
              @error="handleImageError"
            />
            <div v-if="image.caption" class="image-caption">
              {{ image.caption }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation arrows in image carousel -->
      <button 
        v-if="showArrows && images.length > 1"
        class="carousel-arrow carousel-arrow-prev" 
        @click="prevSlide"
        :disabled="currentIndex === 0 && !loop"
      >
        &#8249;
      </button>
      <button 
        v-if="showArrows && images.length > 1"
        class="carousel-arrow carousel-arrow-next" 
        @click="nextSlide"
        :disabled="currentIndex === images.length - 1 && !loop"
      >
        &#8250;
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageCarousel',
  props: {
    images: {
      type: Array,
      required: true,
      default: () => []
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    autoplayInterval: {
      type: Number,
      default: 3000
    },
    loop: {
      type: Boolean,
      default: true
    },
    showArrows: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentIndex: 0,
      autoplayTimer: null
    }
  },
  mounted() {
    if (this.autoplay && this.images.length > 1) {
      this.startAutoplay();
    }
  },
  beforeDestroy() {
    this.stopAutoplay();
  },
  methods: {
    nextSlide() {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++;
      } else if (this.loop) {
        this.currentIndex = 0;
      }
      this.resetAutoplay();
    },
    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else if (this.loop) {
        this.currentIndex = this.images.length - 1;
      }
      this.resetAutoplay();
    },
    goToSlide(index) {
      this.currentIndex = index;
      this.resetAutoplay();
    },
    startAutoplay() {
      if (this.autoplay && this.images.length > 1) {
        this.autoplayTimer = setInterval(() => {
          this.nextSlide();
        }, this.autoplayInterval);
      }
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    resetAutoplay() {
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    },
    handleImageError(event) {
      console.warn('Failed to load image:', event.target.src);
    }
  },
  watch: {
    images: {
      handler() {
        this.currentIndex = 0;
        if (this.autoplay) {
          this.resetAutoplay();
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.image-carousel {
  width: 100%;
  position: relative;
}

.carousel-container {
  position: relative;
  width: 450px;
  height: 450px;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.carousel-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.5s ease-in-out;
}

.carousel-slide {
  min-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background-color: #f8f9fa;
}

.image-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 2.5));
  color: white;
  padding: 35px;
  font-size: 16px;
  text-align: center;
  font-weight: 500;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-arrow:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8);
  transform: translateY(-50%) scale(1.1);
}

.carousel-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel-arrow-prev {
  left: 15px;
}

.carousel-arrow-next {
  right: 15px;
}

/* Responsive design */
@media screen and (max-width: 768px) {
  .carousel-arrow {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  
  .carousel-arrow-prev {
    left: 10px;
  }
  
  .carousel-arrow-next {
    right: 10px;
  }
  
  .image-caption {
    padding: 15px;
    font-size: 12px;
  }
  
  .carousel-container {
    width: 350px;
    height: 350px;
  }
}

@media screen and (max-width: 480px) {
  .carousel-arrow {
    width: 35px;
    height: 35px;
    font-size: 18px;
  }
  
  .image-caption {
    padding: 10px;
    font-size: 11px;
  }
  .carousel-container {
    width: 250px;
    height: 250px;
  }
}
</style> 